import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { AuditProfile, ServiceContext } from "@prolarus/shared";
import { tryCreate, tryDelete, tryGet, tryList, tryUpdate, tryUpdatePost, toQueryString } from "./_shared";

export type PicklistTable = "list_basins" | "list_regions" | "list_segments";

export type Basin = Tables<"list_basins">;
export type Region = Tables<"list_regions">;
export type Segment = Tables<"list_segments">;

export type BasinInsert = Omit<TablesInsert<"list_basins">, "company_id">;
export type RegionInsert = Omit<TablesInsert<"list_regions">, "company_id">;
export type SegmentInsert = Omit<TablesInsert<"list_segments">, "company_id">;

export type BasinUpdate = TablesUpdate<"list_basins">;
export type RegionUpdate = TablesUpdate<"list_regions">;
export type SegmentUpdate = TablesUpdate<"list_segments">;

export type PicklistRecord = Basin | Region | Segment;

type WithAuditNames<T> = T & {
  created_by_profile: AuditProfile | null;
  updated_by_profile: AuditProfile | null;
};

function buildPicklist<T extends PicklistRecord, TInsert extends Record<string, unknown>, TUpdate extends Record<string, unknown>>(
  table: PicklistTable,
) {
  const base = `/api/picklists/${table}`;
  return {
    get(id: string) {
      return tryGet<T>(`${base}/${encodeURIComponent(id)}`);
    },
    getWithAuditNames(id: string) {
      return tryGet<WithAuditNames<T>>(
        `${base}/${encodeURIComponent(id)}${toQueryString({ withAuditNames: true })}`,
      );
    },
    list(_companyId: string) {
      return tryList<T>(base);
    },
    listActive(_companyId: string) {
      return tryList<T>(`${base}?activeOnly=true`);
    },
    getDefault(_companyId: string) {
      return tryGet<T>(`${base}/default`);
    },
    async getNextSequence(_companyId: string): Promise<number> {
      const result = await tryGet<number>(`${base}/next-sequence`);
      return result.data ?? 1;
    },
    create(data: TInsert, _context: ServiceContext) {
      return tryCreate<T>(base, data);
    },
    update(id: string, data: TUpdate, _snapshot: Record<string, unknown>, _context: ServiceContext) {
      return tryUpdate<T>(`${base}/${encodeURIComponent(id)}`, data);
    },
    delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
      return tryDelete(`${base}/${encodeURIComponent(id)}`);
    },
    toggleActive(id: string, isActive: boolean, previousIsActive: boolean, _context: ServiceContext) {
      return tryUpdatePost<T>(`${base}/${encodeURIComponent(id)}/active`, {
        is_active: isActive,
        previous_is_active: previousIsActive,
      });
    },
    setDefault(id: string, _companyId: string, _context: ServiceContext) {
      return tryUpdatePost<T>(`${base}/${encodeURIComponent(id)}/default`, {});
    },
  };
}

export const basinService = buildPicklist<Basin, BasinInsert, BasinUpdate>("list_basins");
export const regionService = buildPicklist<Region, RegionInsert, RegionUpdate>("list_regions");
export const segmentService = buildPicklist<Segment, SegmentInsert, SegmentUpdate>("list_segments");

export const picklistServices = {
  list_basins: basinService,
  list_regions: regionService,
  list_segments: segmentService,
} as const;
